/**
 * Analytics consent state for LunarHeart.
 * Consent decisions persist in localStorage; GA4 initialization is gated on `hasAnalyticsConsent()`.
 * Application Insights reliability telemetry (page views, web vitals) is not gated — it does not
 * carry user-input content or PII and is classified as operational, not marketing, telemetry.
 */

export type AnalyticsConsentState = 'granted' | 'denied' | 'unset'

const CONSENT_CHANGED_EVENT = 'lunarheart:analytics-consent-changed'
export const ANALYTICS_CONSENT_STORAGE_KEY = 'lunarheart:analytics-consent'

function readStoredConsent(): AnalyticsConsentState {
  if (typeof globalThis.window === 'undefined') return 'unset'

  try {
    const stored = globalThis.window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY)
    if (stored === 'granted') return 'granted'
    if (stored === 'denied') return 'denied'
  } catch {
    // fail closed if storage is unavailable
  }

  return 'unset'
}

export function hasAnalyticsConsent(): boolean {
  return readStoredConsent() === 'granted'
}

export function getAnalyticsConsentBlockedReason(): string {
  const state = readStoredConsent()
  if (state === 'denied') return 'consent_denied'
  return 'consent_not_granted'
}

export function persistAnalyticsConsentDecision(granted: boolean): void {
  if (typeof globalThis.window === 'undefined') return

  try {
    globalThis.window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied')
  } catch {
    // fail closed if storage is unavailable
  }

  globalThis.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { granted } }))
}

export function subscribeAnalyticsConsentChanges(onChange: (granted: boolean) => void): () => void {
  if (typeof globalThis.window === 'undefined') return () => {}

  const handler = (event: Event): void => {
    const detail = (event as CustomEvent<{ granted: boolean }>).detail
    onChange(detail.granted)
  }

  globalThis.addEventListener(CONSENT_CHANGED_EVENT, handler)
  return () => globalThis.removeEventListener(CONSENT_CHANGED_EVENT, handler)
}
